import spriteLibraryContent from '../libraries/sprites.json';
import randomizeSpritePosition from '../randomize-sprite-position';

const escapeXml = value => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const normalizeName = value => String(value || '').trim().toLowerCase();

const numberOrDefault = (value, fallback) => (typeof value === 'number' ? value : fallback);
const textOrDefault = (value, fallback) => (value === undefined || value === null || value === '' ? fallback : value);
const AI_SCRIPT_FIELD = 'MAIPAI_AI_SCRIPT';
const motionTargetValue = target => (target === 'random-position' ? '_random_' : '_mouse_');
const hexColorOrDefault = (value, fallback) => /^#[0-9a-fA-F]{6}$/.test(String(value || '')) ?
    String(value) :
    fallback;
const SCALAR_VARIABLE_TYPE = '';

const normalizeSpriteLibraryItem = item => {
    const sprite = JSON.parse(JSON.stringify(item || {}));
    delete sprite.category;
    sprite.isStage = false;
    sprite.variables = sprite.variables || {};
    sprite.lists = sprite.lists || {};
    sprite.broadcasts = sprite.broadcasts || {};
    sprite.blocks = sprite.blocks || {};
    sprite.comments = sprite.comments || {};
    if (typeof sprite.currentCostume !== 'number') sprite.currentCostume = 0;
    if (typeof sprite.volume !== 'number') sprite.volume = 100;
    if (typeof sprite.visible !== 'boolean') sprite.visible = true;
    if (typeof sprite.size !== 'number') sprite.size = 100;
    if (typeof sprite.direction !== 'number') sprite.direction = 90;
    if (typeof sprite.draggable !== 'boolean') sprite.draggable = false;
    sprite.rotationStyle = sprite.rotationStyle || 'all around';
    sprite.costumes = (sprite.costumes || []).map((costume, index) => {
        const nextCostume = Object.assign({}, costume);
        const dataFormat = nextCostume.dataFormat || nextCostume.format || 'png';
        const md5ext = nextCostume.md5ext || nextCostume.md5 || nextCostume.baseLayerMD5 ||
            (nextCostume.assetId ? `${nextCostume.assetId}.${dataFormat}` : '');
        nextCostume.name = nextCostume.name || nextCostume.costumeName || `costume${index + 1}`;
        nextCostume.md5ext = md5ext;
        if (!nextCostume.assetId && md5ext) nextCostume.assetId = md5ext.split('.')[0];
        nextCostume.dataFormat = dataFormat;
        return nextCostume;
    });
    sprite.sounds = (sprite.sounds || []).map((sound, index) => {
        const nextSound = Object.assign({}, sound);
        const dataFormat = nextSound.dataFormat || 'wav';
        const md5ext = nextSound.md5ext || nextSound.md5 ||
            (nextSound.assetId ? `${nextSound.assetId}.${dataFormat}` : '');
        nextSound.name = nextSound.name || nextSound.soundName || `sound${index + 1}`;
        nextSound.md5ext = md5ext;
        if (!nextSound.assetId && md5ext) nextSound.assetId = md5ext.split('.')[0];
        nextSound.dataFormat = dataFormat;
        if (typeof nextSound.format === 'undefined') nextSound.format = '';
        return nextSound;
    });
    return sprite;
};

const findLibrarySprite = spriteName => {
    const normalized = normalizeName(spriteName);
    return spriteLibraryContent.find(sprite => normalizeName(sprite.name) === normalized) ||
        spriteLibraryContent.find(sprite => normalizeName(sprite.name) === 'cat') ||
        spriteLibraryContent[0];
};

const getCurrentTargetNames = vm => vm.runtime.targets
    .filter(target => target && !target.isStage)
    .map(target => target.getName());

const findTargetByName = (vm, name) => {
    const normalized = normalizeName(name);
    return vm.runtime.targets.find(target =>
        target && !target.isStage && normalizeName(target.getName()) === normalized
    );
};

let blockIdCounter = 0;
const makeBlockId = () => `maipai_ai_${Date.now()}_${blockIdCounter++}_${Math.random().toString(36).slice(2, 8)}`;

const field = (name, value, id) => ({
    name,
    id,
    value: String(value)
});

const makeBlock = (opcode, overrides = {}) => Object.assign({
    id: makeBlockId(),
    opcode,
    inputs: {},
    fields: {},
    next: null,
    topLevel: false,
    parent: null,
    shadow: false,
    x: 0,
    y: 0
}, overrides);

const pushValueShadow = (blocks, parentBlock, inputName, opcode, fieldName, value) => {
    const shadow = makeBlock(opcode, {
        parent: parentBlock.id,
        shadow: true,
        fields: {
            [fieldName]: field(fieldName, value)
        }
    });
    blocks.push(shadow);
    parentBlock.inputs[inputName] = {
        name: inputName,
        block: shadow.id,
        shadow: shadow.id
    };
};

const getStageTarget = vm => vm && vm.runtime && typeof vm.runtime.getTargetForStage === 'function' ?
    vm.runtime.getTargetForStage() :
    null;

const ensureVariable = (context, variableName) => {
    const name = String(variableName || 'score').trim() || 'score';
    const variableTarget = context.variableTarget || context.target;
    if (variableTarget && typeof variableTarget.lookupVariableByNameAndType === 'function') {
        const existing = variableTarget.lookupVariableByNameAndType(name, SCALAR_VARIABLE_TYPE, true);
        if (existing) return existing;
    }

    const id = makeBlockId();
    if (variableTarget && typeof variableTarget.createVariable === 'function') {
        variableTarget.createVariable(id, name, SCALAR_VARIABLE_TYPE, false);
        if (variableTarget.variables && variableTarget.variables[id]) return variableTarget.variables[id];
    }

    if (variableTarget) {
        variableTarget.variables = variableTarget.variables || {};
        variableTarget.variables[id] = {
            id,
            name,
            type: SCALAR_VARIABLE_TYPE,
            value: 0,
            isCloud: false
        };
        return variableTarget.variables[id];
    }

    return {id, name, type: SCALAR_VARIABLE_TYPE};
};

const variableField = (context, variableName) => {
    const variable = ensureVariable(context, variableName);
    return field('VARIABLE', variable.name || variableName || 'score', variable.id || variableName || 'score');
};

const createTouchingSpriteConditionBlock = (spriteName, blocks, parentId) => {
    const condition = makeBlock('sensing_touchingobject', {
        parent: parentId
    });
    pushValueShadow(
        blocks,
        condition,
        'TOUCHINGOBJECTMENU',
        'sensing_touchingobjectmenu',
        'TOUCHINGOBJECTMENU',
        textOrDefault(spriteName, '_edge_')
    );
    blocks.push(condition);
    return condition;
};

const createActionBlock = (action, blocks, context = {}) => {
    let block;
    switch (action.type) {
    case 'moveSteps':
        block = makeBlock('motion_movesteps');
        pushValueShadow(blocks, block, 'STEPS', 'math_number', 'NUM', numberOrDefault(action.steps, 10));
        break;
    case 'turnRight':
        block = makeBlock('motion_turnright');
        pushValueShadow(blocks, block, 'DEGREES', 'math_number', 'NUM', numberOrDefault(action.degrees, 15));
        break;
    case 'turnLeft':
        block = makeBlock('motion_turnleft');
        pushValueShadow(blocks, block, 'DEGREES', 'math_number', 'NUM', numberOrDefault(action.degrees, 15));
        break;
    case 'goTo':
        block = makeBlock('motion_gotoxy');
        pushValueShadow(blocks, block, 'X', 'math_number', 'NUM', numberOrDefault(action.x, 0));
        pushValueShadow(blocks, block, 'Y', 'math_number', 'NUM', numberOrDefault(action.y, 0));
        break;
    case 'goToTarget':
        block = makeBlock('motion_goto');
        pushValueShadow(blocks, block, 'TO', 'motion_goto_menu', 'TO', motionTargetValue(action.target));
        break;
    case 'goToSprite':
        block = makeBlock('motion_goto');
        pushValueShadow(blocks, block, 'TO', 'motion_goto_menu', 'TO', textOrDefault(action.spriteName, 'Cat'));
        break;
    case 'glideToTarget':
        block = makeBlock('motion_glideto');
        pushValueShadow(blocks, block, 'SECS', 'math_number', 'NUM', numberOrDefault(action.seconds, 0.2));
        pushValueShadow(blocks, block, 'TO', 'motion_glideto_menu', 'TO', motionTargetValue(action.target));
        break;
    case 'changeY':
        block = makeBlock('motion_changeyby');
        pushValueShadow(blocks, block, 'DY', 'math_number', 'NUM', numberOrDefault(action.dy, 10));
        break;
    case 'changeX':
        block = makeBlock('motion_changexby');
        pushValueShadow(blocks, block, 'DX', 'math_number', 'NUM', numberOrDefault(action.dx, 10));
        break;
    case 'say':
        block = makeBlock('looks_sayforsecs');
        pushValueShadow(blocks, block, 'MESSAGE', 'text', 'TEXT', textOrDefault(action.text, '你好'));
        pushValueShadow(blocks, block, 'SECS', 'math_number', 'NUM', numberOrDefault(action.seconds, 2));
        break;
    case 'wait':
        block = makeBlock('control_wait');
        pushValueShadow(blocks, block, 'DURATION', 'math_positive_number', 'NUM', numberOrDefault(action.seconds, 1));
        break;
    case 'show':
        block = makeBlock('looks_show');
        break;
    case 'hide':
        block = makeBlock('looks_hide');
        break;
    case 'nextCostume':
        block = makeBlock('looks_nextcostume');
        break;
    case 'switchCostume':
        block = makeBlock('looks_switchcostumeto');
        pushValueShadow(blocks, block, 'COSTUME', 'looks_costume', 'COSTUME', textOrDefault(action.costumeName, 'costume1'));
        break;
    case 'playSound':
        block = makeBlock('sound_play');
        pushValueShadow(blocks, block, 'SOUND_MENU', 'sound_sounds_menu', 'SOUND_MENU', textOrDefault(action.soundName, 'pop'));
        break;
    case 'broadcast':
        block = makeBlock('event_broadcast');
        pushValueShadow(blocks, block, 'BROADCAST_INPUT', 'event_broadcast_menu', 'BROADCAST_OPTION', textOrDefault(action.message, 'message1'));
        break;
    case 'setVariable':
        block = makeBlock('data_setvariableto', {
            fields: {
                VARIABLE: variableField(context, action.variableName)
            }
        });
        pushValueShadow(blocks, block, 'VALUE', 'math_number', 'NUM', numberOrDefault(action.value, 0));
        break;
    case 'changeVariable':
        block = makeBlock('data_changevariableby', {
            fields: {
                VARIABLE: variableField(context, action.variableName)
            }
        });
        pushValueShadow(blocks, block, 'VALUE', 'math_number', 'NUM', numberOrDefault(action.value, 1));
        break;
    case 'penClear':
        block = makeBlock('pen_clear');
        break;
    case 'penDown':
        block = makeBlock('pen_penDown');
        break;
    case 'penUp':
        block = makeBlock('pen_penUp');
        break;
    case 'setPenColor':
        block = makeBlock('pen_setPenColorToColor');
        pushValueShadow(blocks, block, 'COLOR', 'colour_picker', 'COLOUR', hexColorOrDefault(action.color, '#ff6680'));
        break;
    case 'changePenColor':
        block = makeBlock('pen_changePenColorParamBy', {
            fields: {
                COLOR_PARAM: field('COLOR_PARAM', 'color')
            }
        });
        pushValueShadow(blocks, block, 'VALUE', 'math_number', 'NUM', numberOrDefault(action.amount, 10));
        break;
    case 'setPenSize':
        block = makeBlock('pen_setPenSizeTo');
        pushValueShadow(blocks, block, 'SIZE', 'math_number', 'NUM', numberOrDefault(action.size, 3));
        break;
    case 'changePenSize':
        block = makeBlock('pen_changePenSizeBy');
        pushValueShadow(blocks, block, 'SIZE', 'math_number', 'NUM', numberOrDefault(action.amount, 1));
        break;
    case 'repeat':
        block = makeBlock('control_repeat');
        pushValueShadow(blocks, block, 'TIMES', 'math_whole_number', 'NUM', numberOrDefault(action.times, 10));
        {
            const nested = actionsToBlocks(action.actions || [], block.id, blocks, context);
            if (nested.firstId) {
                block.inputs.SUBSTACK = {
                    name: 'SUBSTACK',
                    block: nested.firstId,
                    shadow: null
                };
            }
        }
        break;
    case 'forever':
        block = makeBlock('control_forever');
        {
            const nested = actionsToBlocks(action.actions || [], block.id, blocks, context);
            if (nested.firstId) {
                block.inputs.SUBSTACK = {
                    name: 'SUBSTACK',
                    block: nested.firstId,
                    shadow: null
                };
            }
        }
        break;
    case 'ifTouchingSprite':
        block = makeBlock('control_if');
        {
            const condition = createTouchingSpriteConditionBlock(action.spriteName, blocks, block.id);
            block.inputs.CONDITION = {
                name: 'CONDITION',
                block: condition.id,
                shadow: null
            };
            const nested = actionsToBlocks(action.actions || [], block.id, blocks, context);
            if (nested.firstId) {
                block.inputs.SUBSTACK = {
                    name: 'SUBSTACK',
                    block: nested.firstId,
                    shadow: null
                };
            }
        }
        break;
    case 'ifOnEdgeBounce':
        block = makeBlock('motion_ifonedgebounce');
        break;
    default:
        return null;
    }
    blocks.push(block);
    return block;
};

const actionsToBlocks = (actions, parentId, blocks, context = {}) => {
    let firstId = null;
    let previous = null;
    for (const action of actions) {
        const block = createActionBlock(action, blocks, context);
        if (!block) continue;
        block.parent = previous ? previous.id : parentId;
        if (!firstId) firstId = block.id;
        if (previous) previous.next = block.id;
        previous = block;
    }
    return {firstId, lastId: previous && previous.id};
};

const scriptToBlocks = (script, index, context = {}) => {
    const blocks = [];
    const event = script.event || {type: 'greenFlag'};
    let eventBlock = makeBlock('event_whenflagclicked', {
        topLevel: true,
        x: 40 + (index % 3) * 260,
        y: 40 + Math.floor(index / 3) * 220
    });
    if (event.type === 'keyPressed') {
        eventBlock = makeBlock('event_whenkeypressed', {
            topLevel: true,
            x: 40 + (index % 3) * 260,
            y: 40 + Math.floor(index / 3) * 220,
            fields: {
                KEY_OPTION: field('KEY_OPTION', textOrDefault(event.key, 'space'))
            }
        });
    } else if (event.type === 'spriteClicked') {
        eventBlock = makeBlock('event_whenthisspriteclicked', {
            topLevel: true,
            x: 40 + (index % 3) * 260,
            y: 40 + Math.floor(index / 3) * 220
        });
    } else if (event.type === 'broadcastReceived') {
        eventBlock = makeBlock('event_whenbroadcastreceived', {
            topLevel: true,
            x: 40 + (index % 3) * 260,
            y: 40 + Math.floor(index / 3) * 220,
            fields: {
                BROADCAST_OPTION: field('BROADCAST_OPTION', textOrDefault(event.message, 'message1'))
            }
        });
    }
    eventBlock.fields = Object.assign({}, eventBlock.fields, {
        [AI_SCRIPT_FIELD]: field(AI_SCRIPT_FIELD, '1')
    });
    const actionChain = actionsToBlocks(script.actions || [], eventBlock.id, blocks, context);
    eventBlock.next = actionChain.firstId;
    blocks.push(eventBlock);
    return blocks;
};

const spriteScriptsToBlocks = (spritePlan, context = {}) =>
    (spritePlan.scripts || []).flatMap((script, index) => scriptToBlocks(script, index, context));

const valueShadow = (type, fieldName, value) =>
    `<shadow type="${type}"><field name="${fieldName}">${escapeXml(value)}</field></shadow>`;

const valueInput = (name, type, fieldName, value) =>
    `<value name="${name}">${valueShadow(type, fieldName, value)}</value>`;

const actionToBlockXml = action => {
    switch (action.type) {
    case 'moveSteps':
        return `<block type="motion_movesteps">${valueInput('STEPS', 'math_number', 'NUM', numberOrDefault(action.steps, 10))}</block>`;
    case 'turnRight':
        return `<block type="motion_turnright">${valueInput('DEGREES', 'math_number', 'NUM', numberOrDefault(action.degrees, 15))}</block>`;
    case 'turnLeft':
        return `<block type="motion_turnleft">${valueInput('DEGREES', 'math_number', 'NUM', numberOrDefault(action.degrees, 15))}</block>`;
    case 'goTo':
        return `<block type="motion_gotoxy">${valueInput('X', 'math_number', 'NUM', numberOrDefault(action.x, 0))}${valueInput('Y', 'math_number', 'NUM', numberOrDefault(action.y, 0))}</block>`;
    case 'goToTarget':
        return `<block type="motion_goto">${valueInput('TO', 'motion_goto_menu', 'TO', motionTargetValue(action.target))}</block>`;
    case 'goToSprite':
        return `<block type="motion_goto">${valueInput('TO', 'motion_goto_menu', 'TO', textOrDefault(action.spriteName, 'Cat'))}</block>`;
    case 'glideToTarget':
        return `<block type="motion_glideto">${valueInput('SECS', 'math_number', 'NUM', numberOrDefault(action.seconds, 0.2))}${valueInput('TO', 'motion_glideto_menu', 'TO', motionTargetValue(action.target))}</block>`;
    case 'changeY':
        return `<block type="motion_changeyby">${valueInput('DY', 'math_number', 'NUM', numberOrDefault(action.dy, 10))}</block>`;
    case 'changeX':
        return `<block type="motion_changexby">${valueInput('DX', 'math_number', 'NUM', numberOrDefault(action.dx, 10))}</block>`;
    case 'say':
        return `<block type="looks_sayforsecs">${valueInput('MESSAGE', 'text', 'TEXT', textOrDefault(action.text, '你好'))}${valueInput('SECS', 'math_number', 'NUM', numberOrDefault(action.seconds, 2))}</block>`;
    case 'wait':
        return `<block type="control_wait">${valueInput('DURATION', 'math_positive_number', 'NUM', numberOrDefault(action.seconds, 1))}</block>`;
    case 'show':
        return '<block type="looks_show"></block>';
    case 'hide':
        return '<block type="looks_hide"></block>';
    case 'nextCostume':
        return '<block type="looks_nextcostume"></block>';
    case 'switchCostume':
        return `<block type="looks_switchcostumeto"><value name="COSTUME"><shadow type="looks_costume"><field name="COSTUME">${escapeXml(textOrDefault(action.costumeName, 'costume1'))}</field></shadow></value></block>`;
    case 'playSound':
        return `<block type="sound_play"><value name="SOUND_MENU"><shadow type="sound_sounds_menu"><field name="SOUND_MENU">${escapeXml(textOrDefault(action.soundName, 'pop'))}</field></shadow></value></block>`;
    case 'broadcast':
        return `<block type="event_broadcast"><value name="BROADCAST_INPUT"><shadow type="event_broadcast_menu"><field name="BROADCAST_OPTION">${escapeXml(textOrDefault(action.message, 'message1'))}</field></shadow></value></block>`;
    case 'setVariable':
        return `<block type="data_setvariableto"><field name="VARIABLE">${escapeXml(textOrDefault(action.variableName, 'score'))}</field>${valueInput('VALUE', 'math_number', 'NUM', numberOrDefault(action.value, 0))}</block>`;
    case 'changeVariable':
        return `<block type="data_changevariableby"><field name="VARIABLE">${escapeXml(textOrDefault(action.variableName, 'score'))}</field>${valueInput('VALUE', 'math_number', 'NUM', numberOrDefault(action.value, 1))}</block>`;
    case 'penClear':
        return '<block type="pen_clear"></block>';
    case 'penDown':
        return '<block type="pen_penDown"></block>';
    case 'penUp':
        return '<block type="pen_penUp"></block>';
    case 'setPenColor':
        return `<block type="pen_setPenColorToColor">${valueInput('COLOR', 'colour_picker', 'COLOUR', hexColorOrDefault(action.color, '#ff6680'))}</block>`;
    case 'changePenColor':
        return `<block type="pen_changePenColorParamBy"><field name="COLOR_PARAM">color</field>${valueInput('VALUE', 'math_number', 'NUM', numberOrDefault(action.amount, 10))}</block>`;
    case 'setPenSize':
        return `<block type="pen_setPenSizeTo">${valueInput('SIZE', 'math_number', 'NUM', numberOrDefault(action.size, 3))}</block>`;
    case 'changePenSize':
        return `<block type="pen_changePenSizeBy">${valueInput('SIZE', 'math_number', 'NUM', numberOrDefault(action.amount, 1))}</block>`;
    case 'repeat':
        return `<block type="control_repeat">${valueInput('TIMES', 'math_whole_number', 'NUM', numberOrDefault(action.times, 10))}<statement name="SUBSTACK">${actionsToNestedXml(action.actions || [])}</statement></block>`;
    case 'forever':
        return `<block type="control_forever"><statement name="SUBSTACK">${actionsToNestedXml(action.actions || [])}</statement></block>`;
    case 'ifTouchingSprite':
        return `<block type="control_if"><value name="CONDITION"><block type="sensing_touchingobject"><value name="TOUCHINGOBJECTMENU"><shadow type="sensing_touchingobjectmenu"><field name="TOUCHINGOBJECTMENU">${escapeXml(textOrDefault(action.spriteName, '_edge_'))}</field></shadow></value></block></value><statement name="SUBSTACK">${actionsToNestedXml(action.actions || [])}</statement></block>`;
    case 'ifOnEdgeBounce':
        return '<block type="motion_ifonedgebounce"></block>';
    default:
        return '';
    }
};

const chainBlocks = blocks => {
    const [first, ...rest] = blocks.filter(Boolean);
    if (!first) return '';
    if (!rest.length) return first;
    return first.replace('</block>', `<next>${chainBlocks(rest)}</next></block>`);
};

const actionsToNestedXml = actions => chainBlocks(actions.map(actionToBlockXml));

const scriptToXml = (script, index) => {
    const event = script.event || {type: 'greenFlag'};
    let eventType = 'event_whenflagclicked';
    let eventContent = '';
    if (event.type === 'keyPressed') {
        eventType = 'event_whenkeypressed';
        eventContent = `<field name="KEY_OPTION">${escapeXml(textOrDefault(event.key, 'space'))}</field>`;
    } else if (event.type === 'spriteClicked') {
        eventType = 'event_whenthisspriteclicked';
    } else if (event.type === 'broadcastReceived') {
        eventType = 'event_whenbroadcastreceived';
        eventContent = `<field name="BROADCAST_OPTION">${escapeXml(textOrDefault(event.message, 'message1'))}</field>`;
    }

    const actionXml = actionsToNestedXml(script.actions || []);
    const x = 40 + (index % 3) * 260;
    const y = 40 + Math.floor(index / 3) * 220;
    return `<block type="${eventType}" x="${x}" y="${y}">${eventContent}<field name="${AI_SCRIPT_FIELD}">1</field>${actionXml ? `<next>${actionXml}</next>` : ''}</block>`;
};

const spriteScriptsToXml = spritePlan =>
    `<xml xmlns="https://developers.google.com/blockly/xml">${(spritePlan.scripts || [])
        .map(scriptToXml)
        .join('')}</xml>`;

const waitForBridge = () => new Promise((resolve, reject) => {
    let attempts = 0;
    const tick = () => {
        if (window.MaipaiScratchAiBridge && window.MaipaiScratchAiBridge.insertXml) {
            resolve(window.MaipaiScratchAiBridge);
            return;
        }
        attempts += 1;
        if (attempts > 50) {
            reject(new Error('Scratch blocks workspace is not ready'));
            return;
        }
        window.setTimeout(tick, 100);
    };
    tick();
});

const waitForWorkspaceUpdate = vm => new Promise(resolve => {
    let resolved = false;
    const done = () => {
        if (resolved) return;
        resolved = true;
        vm.removeListener('workspaceUpdate', done);
        resolve();
    };
    vm.on('workspaceUpdate', done);
    window.setTimeout(done, 1200);
});

const addOrSelectSprite = async (vm, spritePlan) => {
    const existing = findTargetByName(vm, spritePlan.spriteName) ||
        findTargetByName(vm, spritePlan.displayName);
    if (existing) {
        vm.setEditingTarget(existing.id);
        await waitForWorkspaceUpdate(vm);
        return existing;
    }

    const beforeNames = new Set(getCurrentTargetNames(vm));
    const librarySprite = normalizeSpriteLibraryItem(findLibrarySprite(spritePlan.spriteName));
    randomizeSpritePosition(librarySprite);
    if (typeof spritePlan.x === 'number') librarySprite.x = spritePlan.x;
    if (typeof spritePlan.y === 'number') librarySprite.y = spritePlan.y;
    if (typeof spritePlan.size === 'number') librarySprite.size = spritePlan.size;
    if (typeof spritePlan.direction === 'number') librarySprite.direction = spritePlan.direction;

    await vm.addSprite(JSON.stringify(librarySprite));
    const added = vm.runtime.targets.find(target =>
        target && !target.isStage && !beforeNames.has(target.getName())
    ) || findTargetByName(vm, librarySprite.name);
    if (!added) throw new Error(`Unable to add sprite ${spritePlan.spriteName}`);
    vm.setEditingTarget(added.id);
    await waitForWorkspaceUpdate(vm);
    return added;
};

const isAiGeneratedTopBlock = block =>
    block &&
    block.topLevel &&
    block.fields &&
    block.fields[AI_SCRIPT_FIELD] &&
    block.fields[AI_SCRIPT_FIELD].value === '1';

const clearPreviousAiScripts = target => {
    if (!target || !target.blocks || !target.blocks._blocks) return;
    const aiTopBlockIds = Object.keys(target.blocks._blocks)
        .filter(blockId => isAiGeneratedTopBlock(target.blocks._blocks[blockId]));
    aiTopBlockIds.forEach(blockId => target.blocks.deleteBlock(blockId));
};

export const applyScratchAiPlan = async (vm, plan) => {
    await waitForBridge();
    const variableTarget = getStageTarget(vm);
    for (const spritePlan of plan.sprites || []) {
        const target = await addOrSelectSprite(vm, spritePlan);
        clearPreviousAiScripts(target);
        const blocks = spriteScriptsToBlocks(spritePlan, {
            vm,
            target,
            variableTarget: variableTarget || target
        });
        await vm.shareBlocksToTarget(blocks, target.id);
        vm.refreshWorkspace();
        await new Promise(resolve => window.setTimeout(resolve, 80));
    }
};
